<template>
  <main>
    <Loader
      v-if="loaderStore.load[ELoader.MATERIAL]"
      :loader="loaderStore.load[ELoader.MATERIAL]"
    />

    <section v-else-if="storeMaterial.currentMaterial?.file">
      <header class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
        <div class="flex flex-col sm:flex-row sm:items-center sm:gap-5 w-full sm:w-auto">
          <h5 class="text-lg sm:text-xl font-semibold break-words">
            {{ storeMaterial.currentMaterial.name }}
          </h5>
          <small
            v-if="isDone"
            class="hidden sm:inline px-2 py-1 bg-green-500 text-white rounded-lg border text-xs"
          >
            {{ "Concluído" }}
          </small>
          <div
            id="tips"
            v-if="!isDone && storeMaterial.isVideo"
            class="group flex justify-start"
          >
            <i
              class="pi pi-exclamation-circle cursor-pointer text-white bg-h-warning-400 px-1 text-xl rounded-full hover:scale-75 transition-all animate-bounce-short"
            />
            <div
              class="hidden absolute transition-all group-hover:flex group-focus:flex bg-white p-3 w-64 mt-8 rounded-lg z-50 shadow-md border overflow-hidden break-words whitespace-normal"
            >
              <p
                v-html="
                  'O material do tipo <b>vídeo só será considerado concluído</b>, se o aluno <b>assistir pelo menos 90%</b> de seu conteúdo.'
                "
              ></p>
            </div>
          </div>
        </div>
        <div class="flex sm:hidden items-center justify-between gap-2 mt-1">
          <small
            v-if="isDone"
            class="px-2 py-1 bg-green-500 text-white rounded-lg border text-xs"
          >
            {{ "Concluído" }}
          </small>
          <div class="flex gap-2 ml-auto">
            <Button
              icon="pi pi-chevron-left"
              class="btn-primary"
              @click.prevent="storeNavigation.prev"
            />
            <Button
              icon="pi pi-chevron-right"
              class="btn-primary"
              :disabled="storeNavigation.isNextDisabled"
              @click.prevent="storeNavigation.next"
            />
          </div>
        </div>
        <div class="hidden sm:flex gap-2">
          <Button
            icon="pi pi-chevron-left"
            class="btn-primary"
            :disabled="isPrevThrottled"
            @click.prevent="handlePrev"
          />
          <Button
            icon="pi pi-chevron-right"
            class="btn-primary"
            :disabled="storeNavigation.isNextDisabled || isNextThrottled"
            @click.prevent="handleNext"
          />
        </div>
        </header>
      <div v-if="storeMaterial.isVideo" :class="`${flex} mt-4 flex-col z-10`">
        <VideoComponent
          :data="storeMaterial.currentMaterial?.file"
          :idVideo="storeMaterial.currentMaterial?.file?.id"
          :startTime="time"
          :isAdaptativeLearning="false"
          ref="videoComponent"
        />
      </div>
      <div v-if="storeMaterial.isImage" :class="`${flex} mt-4`">
        <Image
          :src="storeMaterial.currentMaterial?.file?.url"
          alt="material-image"
          imageClass="w-full h-[100%] sm:h-[600px] lg:h-[600px] object-cover"
          preview
        />
      </div>
      <div v-if="storeMaterial.isAudio" :class="`${flex} mt-4`">
        <audio
          id="material-audio"
          :src="storeMaterial.currentMaterial?.file?.url"
          class="w-full h-[100%] sm:h-[600px] lg:h-[600px]"
        ></audio>
      </div>
      <div v-if="storeMaterial.isPdf" :class="`${flex} mt-4`">
        <PDFComponent :url="storeMaterial.currentMaterial?.file?.url" />
      </div>
    </section>
  </main>

  <Dialog
    v-model:visible="showAlert"
    :draggable="false"
    :breakpoints="{ '960px': '95vw', '640px': '95vw' }"
    :style="{ width: '25vw', minWidth: '360px' }"
    modal
  >
    <template #header>
      <section class="flex items-center gap-3">
        <i class="pi pi-info-circle !text-3xl text-h-warning-400"></i>
        <h3>
          {{ $t("labels.generic", { pt: `Alerta`, en: `Alert` }) }}
        </h3>
      </section>
    </template>
    <p
      class="text-xl"
      v-html="
        'O material do tipo <b>vídeo só será considerado concluído</b>, se o aluno <b>assistir pelo menos 90%</b> de seu conteúdo.'
      "
    ></p>
  </Dialog>
</template>

<script lang="ts">
import { ELoader, useLoaderStore } from "@/store/linear/loader";
import { useMaterialStore } from "@/store/linear/material";
import { useNavigationStore } from "@/store/linear/navigation";
import { useVideoStore } from "@/store/video";
import { computed, defineComponent, onMounted, ref } from "vue";

export default defineComponent({
  name: "MaterialLinear",
  setup() {
    const storeMaterial = useMaterialStore;
    const storeNavigation = useNavigationStore;
    const loaderStore = useLoaderStore;
    const videoStore = useVideoStore;
    const flex = "w-full md:min-w-[360px] min-h-[300px] flex justify-center";
    const time = ref(0);

    const isDone = computed(
      () => storeMaterial.materials[storeMaterial.currentMaterialIndex]?.done
    );

    const showAlert = ref(false);
    const isNextThrottled = ref(false);
    const isPrevThrottled = ref(false);

    const handleNext = () => {
      if (storeNavigation.isNextDisabled || isNextThrottled.value) return;
      isNextThrottled.value = true;
      try {
        storeNavigation.next();
      } finally {
        setTimeout(() => (isNextThrottled.value = false), 800);
      }
    };

    const handlePrev = () => {
      if (isPrevThrottled.value) return;
      isPrevThrottled.value = true;
      try {
        storeNavigation.prev();
      } finally {
        setTimeout(() => (isPrevThrottled.value = false), 800);
      }
    };

    onMounted(() => {
      loaderStore.start(ELoader.MATERIAL);
      if (localStorage.getItem("alert-shown") === "yes") return;
      if (!isDone.value && storeMaterial.isVideo) {
        showAlert.value = true;
        localStorage.setItem("alert-shown", "yes");
      }
    });

    return {
      storeMaterial,
      storeNavigation,
      loaderStore,
      videoStore,
      flex,
      time,
      isDone,
      showAlert,
      isNextThrottled,
      isPrevThrottled,
      handleNext,
      handlePrev,
      ELoader,
    };
  },
});
</script>
